import { Users, FolderKanban, Clock, CheckCircle } from 'lucide-react'

import { useTeamStats } from '@/hooks'

interface TeamStatsProps {
  teamId: string
}

/**
 * 格式化完成率显示
 * API 返回的 completionRate 可能是小数 (0-1) 或百分比 (0-100)
 * 此函数确保输出始终在 0-100% 范围内
 */
function formatCompletionRate(rate: number): number {
  // 如果值大于 1，假设已经是百分比形式
  const percentage = rate > 1 ? rate : rate * 100
  // 确保在 0-100 范围内
  return Math.min(100, Math.max(0, percentage))
}

/**
 * 团队统计组件
 */
export function TeamStats({ teamId }: TeamStatsProps) {
  const { data: stats, isLoading, error } = useTeamStats(teamId)

  if (isLoading) {
    return <div className="text-muted-foreground">加载中...</div>
  }

  if (error) {
    return <div className="text-destructive">加载失败: {error.message}</div>
  }

  if (!stats) {
    return <div className="text-muted-foreground">暂无统计数据</div>
  }

  return (
    <div className="space-y-6">
      {/* 概览卡片 */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          icon={<Users className="h-5 w-5 text-blue-500" />}
          label="成员数量"
          value={stats.memberCount}
          bgColor="bg-blue-500/10"
        />
        <StatCard
          icon={<FolderKanban className="h-5 w-5 text-green-500" />}
          label="项目数量"
          value={stats.projectCount}
          bgColor="bg-green-500/10"
        />
        <StatCard
          icon={<Clock className="h-5 w-5 text-purple-500" />}
          label="总工时"
          value={`${stats.totalHours}h`}
          bgColor="bg-purple-500/10"
        />
        <StatCard
          icon={<CheckCircle className="h-5 w-5 text-orange-500" />}
          label="任务完成率"
          value={`${formatCompletionRate(stats.taskStats.completionRate).toFixed(1)}%`}
          bgColor="bg-orange-500/10"
        />
      </div>

      {/* 任务统计 */}
      <div className="rounded-lg border bg-card p-4">
        <h3 className="mb-4 font-medium">任务统计</h3>
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="rounded-md bg-muted/50 p-3">
            <p className="text-sm text-muted-foreground">总任务数</p>
            <p className="mt-1 text-2xl font-bold">{stats.taskStats.total}</p>
          </div>
          <div className="rounded-md bg-green-50 p-3">
            <p className="text-sm text-muted-foreground">已完成</p>
            <p className="mt-1 text-2xl font-bold text-green-600">{stats.taskStats.completed}</p>
          </div>
          <div className="rounded-md bg-blue-50 p-3">
            <p className="text-sm text-muted-foreground">进行中</p>
            <p className="mt-1 text-2xl font-bold text-blue-600">{stats.taskStats.inProgress}</p>
          </div>
        </div>
        {/* 进度条 */}
        <div className="mt-4">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">完成进度</span>
            <span className="font-medium">{formatCompletionRate(stats.taskStats.completionRate).toFixed(1)}%</span>
          </div>
          <div className="mt-2 h-2 overflow-hidden rounded-full bg-muted">
            <div
              className="h-full bg-green-500 transition-all"
              style={{ width: `${formatCompletionRate(stats.taskStats.completionRate)}%` }}
            />
          </div>
        </div>
      </div>

      {/* 成员工时分布 */}
      {stats.memberHours.length > 0 && (
        <div className="rounded-lg border bg-card p-4">
          <h3 className="mb-4 font-medium">成员工时分布</h3>
          <div className="space-y-3">
            {stats.memberHours.map((member, index) => {
              const maxHours = Math.max(...stats.memberHours.map(m => m.hours))
              const percentage = maxHours > 0 ? (member.hours / maxHours) * 100 : 0
              return (
                <div key={member.employeeId || index}>
                  <div className="flex items-center justify-between text-sm">
                    <span>{member.employeeName || `员工 ${member.employeeId?.slice(0, 8) || '未知'}`}</span>
                    <span className="font-medium">{member.hours}h</span>
                  </div>
                  <div className="mt-1 h-2 overflow-hidden rounded-full bg-muted">
                    <div
                      className="h-full bg-primary transition-all"
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

interface StatCardProps {
  icon: React.ReactNode
  label: string
  value: string | number
  bgColor: string
}

function StatCard({ icon, label, value, bgColor }: StatCardProps) {
  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="flex items-center gap-3">
        <div className={`rounded-md p-2 ${bgColor}`}>
          {icon}
        </div>
        <div>
          <p className="text-sm text-muted-foreground">{label}</p>
          <p className="text-xl font-bold">{value}</p>
        </div>
      </div>
    </div>
  )
}
